import { queryOptions, useQuery } from "@tanstack/react-query";
import { z } from "zod";

import { restFetcher } from "~/api/fetchers";
import { createFilter } from "~/api/utils";

export const formResponseSchema = z.object({
  id: z.number(),
  json: z.string(),
  name: z.string(),
  status: z.enum(["LIVE", "DRAFT", "SUPERSEDED"]),
  type: z.string(),
  modalityId: z.number().optional(),
  visible: z.boolean(),
});

export type FormResponse = z.infer<typeof formResponseSchema>;

export const formSchema = formResponseSchema.extend({
  json: z.preprocess(
    val => (typeof val === "string" ? JSON.parse(val || "{}") : val),
    z.object({
      form: z.array(z.record(z.string(), z.unknown())),
      schema: z.record(z.string(), z.unknown()),
      meta: z
        .object({
          type: z.enum(["simple"]).optional(),
        })
        .optional(),
    })
  ),
});

export type Form = z.infer<typeof formSchema>;

export function useGetFormQuery(formId: number) {
  return useQuery({
    queryKey: ["form", formId],
    queryFn: async () => {
      const data = await restFetcher<FormResponse>(`/v1/forms/${formId}`);
      return formSchema.parse(data);
    },
    staleTime: Infinity,
  });
}

export type FormsetsFormFilter = {
  type?: string;
  modalityId?: number;
  excludeSystemDefaults?: boolean;
};

export function getFormsetForms(filter: FormsetsFormFilter) {
  const searchParams = createFilter<FormsetsFormFilter>(filter);
  return queryOptions({
    queryKey: ["formset", filter],
    queryFn: async () => {
      const resp = await restFetcher<Array<FormResponse>>(
        `/v1/form/getAllByModalityAndType?${searchParams}`
      );
      return resp.map(item => formSchema.parse(item));
    },
    staleTime: Infinity,
  });
}

export function getReferralForms() {
  return queryOptions({
    queryKey: ["forms", "referral"],
    queryFn: async () => {
      const resp = await restFetcher<Array<FormResponse>>("/v1/form/getByFilters?types=REFERRAL");
      return resp.map(item => formSchema.parse(item)).at(0);
    },
    staleTime: Infinity,
  });
}

export type LatestFormsFilter = {
  type?: "WHANAU_CLIENT_SESSION" | "WHANAU_SESSION" | "CLIENT_SESSION" | "GROUP_SESSION";
  modalityId?: number;
};

export function getLatestForms(filter: LatestFormsFilter) {
  const searchParams = createFilter<LatestFormsFilter>(filter);
  return queryOptions({
    queryKey: ["formset", filter],
    queryFn: async () => {
      const data = await restFetcher<FormResponse>(
        `/v1/form/getLatestByModalityAndType?${searchParams}`
      );
      return formSchema.parse(data);
    },
    staleTime: Infinity,
  });
}
